import { z } from "zod";
import { BASE, ISO } from "./common.ts";

export const WORKFLOW_STAGE = z.enum(["lead", "sale", "onboarding", "delivery", "billing", "retention", "internal"]);
export type WorkflowStage = z.infer<typeof WORKFLOW_STAGE>;

export const WORKFLOW_SCOPE = z.enum(["workspace", "service", "client", "project", "space"]);
export type WorkflowScope = z.infer<typeof WORKFLOW_SCOPE>;

export const WORKFLOW_STATE = z.enum(["published", "draft", "paused"]);
export type WorkflowState = z.infer<typeof WORKFLOW_STATE>;

/**
 * What a box on the canvas is (PHASED-PLAN F9/F11). `condition` is if/else on a rule, `switch` picks one of several
 * `case` edges, `filter` lets the run go on only when its rule holds, `loop` runs its `each` branch once per list item,
 * `wait` pauses for a duration or until an event (with a timeout), `merge` joins parallel branches, `stop` ends the run,
 * `limit` is a rate limit with a yes/no answer, `subflow` calls another workflow, and `approval` asks a person to approve
 * or reject with a comment, and `form` sends a public form to a person and waits for the answer. `frame` only groups
 * boxes on the canvas: it has no lines and the engine never runs it.
 */
export const NODE_KIND = z.enum([
  "trigger", "condition", "action", "human", "delay", "data", "code", "note",
  "switch", "split", "filter", "loop", "wait", "merge", "stop", "limit", "subflow", "approval", "form", "frame",
]);
export type NodeKind = z.infer<typeof NODE_KIND>;

/**
 * Which branch an edge is. `yes`/`no` leave a condition or a rate limit; `error` follows a failed step; `approved`,
 * `rejected` and `escalate` leave an approval (`escalate`: nobody answered in time); `timeout` leaves a wait that ran out;
 * `each` leaves a loop into its body and `done` leaves it after the last item. An edge with no label is the plain next step.
 */
export const EDGE_LABEL = z.enum(["yes", "no", "error", "approved", "rejected", "escalate", "timeout", "each", "done"]);
export type EdgeLabel = z.infer<typeof EDGE_LABEL>;

/** How a rule compares a field with a value. `changed` is true when the field was among the fields the trigger's write changed. */
export const RULE_OP = z.enum(["eq", "ne", "gt", "gte", "lt", "lte", "contains", "in", "empty", "notEmpty", "changed"]);
export type RuleOp = z.infer<typeof RULE_OP>;

/** One comparison: `field` is a path into the run's data ("record.total.amount", "input.plan", "steps.find.count"). */
export const RULE_CONDITION = z.strictObject({
  field: z.string().min(1).max(200),
  op: RULE_OP,
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))]).optional(),
});
export type RuleCondition = z.infer<typeof RULE_CONDITION>;

/** `all` (and) or `any` (or) of comparisons and nested groups: the rule JSON stored in a node's `config.rule`. */
export type RuleGroup = { all: (RuleCondition | RuleGroup)[] } | { any: (RuleCondition | RuleGroup)[] };
export const RULE: z.ZodType<RuleGroup> = z.lazy(() =>
  z.union([
    z.strictObject({ all: z.array(z.union([RULE_CONDITION, RULE])).min(1).max(50) }),
    z.strictObject({ any: z.array(z.union([RULE_CONDITION, RULE])).min(1).max(50) }),
  ]),
);

/** The trigger types a trigger node can have, in `config.event` (any other value there is a record or domain event name). */
export const TRIGGER_TYPES = ["schedule", "date.relative", "webhook", "manual", "field.changed", "workflow.failed"] as const;
export type TriggerType = (typeof TRIGGER_TYPES)[number];

/** Used for workflow runs and for each step inside a run. */
export const RUN_STATE = z.enum(["succeeded", "failed", "queued", "running", "skipped", "cancelled"]);
export type RunState = z.infer<typeof RUN_STATE>;

export const TEMPLATE_CHANNEL = z.enum(["email", "whatsapp", "sms", "portal", "push"]);
export type TemplateChannel = z.infer<typeof TEMPLATE_CHANNEL>;

export const TEMPLATE_APPROVAL = z.enum(["approved", "pending", "rejected"]);
export type TemplateApproval = z.infer<typeof TEMPLATE_APPROVAL>;

/** One box on the canvas. `x` and `y` are canvas pixels, and `config` holds the inspector's text fields. A frame also takes
 *  `w` and `h` (its size in canvas pixels) and `color`, and its label is at most 120 characters. */
export const WORKFLOW_NODE = z.object({
  id: z.string(),
  kind: NODE_KIND,
  app: z.string().optional(),
  label: z.string(),
  detail: z.string().optional(),
  x: z.number(),
  y: z.number(),
  config: z.record(z.string(), z.string()),
  w: z.number().optional(),
  h: z.number().optional(),
  color: z.string().max(20).optional(),
}).refine((n) => n.kind !== "frame" || n.label.length <= 120, { message: "A frame's label is at most 120 characters.", path: ["label"] })
  .refine((n) => n.kind !== "code" || (n.config.source ?? "").length <= 20000, { message: "A code step is at most 20000 characters.", path: ["config"] });
export type WorkflowNode = z.infer<typeof WORKFLOW_NODE>;

/** A line between two nodes. `label` is set on the branches of a condition, approval, wait or loop. */
export const WORKFLOW_EDGE = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  label: EDGE_LABEL.optional(),
  /** On the edges of a `switch`: the value this branch takes. The edge with no `case` is the default branch. */
  case: z.string().optional(),
});
export type WorkflowEdge = z.infer<typeof WORKFLOW_EDGE>;

export const WORKFLOW_VERSION = z.object({
  v: z.number().int(),
  at: ISO,
  by: z.string(),
  note: z.string(),
  published: z.boolean(),
  /** Waiting for an owner or admin to approve it (workflowPublishApproval); the live version is unchanged until then. */
  pending: z.boolean().optional(),
  /** An owner or admin turned it down; it never goes live. */
  rejected: z.boolean().optional(),
});
export type WorkflowVersion = z.infer<typeof WORKFLOW_VERSION>;

/** An automation. `scopeId` names the client, project or service it is limited to, when the scope is not the workspace. */
export const WORKFLOW = BASE.extend({
  name: z.string(),
  stage: WORKFLOW_STAGE,
  scope: WORKFLOW_SCOPE,
  scopeId: z.string().optional(),
  /** A workspace-wide flow this one replaces for the records in its own scope (only when the scope is not "workspace"). */
  replacesId: z.string().optional(),
  state: WORKFLOW_STATE,
  runsMonth: z.number().int(),
  /** 0 to 1. */
  failureRate: z.number().min(0).max(1),
  lastRunAt: ISO.optional(),
  ownerId: z.string(),
  /** The record the builder reads sample values from when a test run shows what each step would do. */
  sampleRecordId: z.string().optional(),
  /** The JSON a webhook or button flow's test run reads as `input.<field>`, pinned by the tester (X16-C806a). */
  sampleInput: z.string().max(65536).optional(),
  version: z.number().int(),
  summary: z.string(),
  /** The recipe (RECIPES in mod-automation) this flow was installed from, so the gallery shows it as installed. */
  recipeId: z.string().optional(),
  nodes: z.array(WORKFLOW_NODE),
  edges: z.array(WORKFLOW_EDGE),
  versions: z.array(WORKFLOW_VERSION),
});
export type Workflow = z.infer<typeof WORKFLOW>;

/** The canvas of one workflow version: its boxes and lines. */
export const WORKFLOW_GRAPH = z.object({
  nodes: z.array(WORKFLOW_NODE),
  edges: z.array(WORKFLOW_EDGE),
});
export type WorkflowGraph = z.infer<typeof WORKFLOW_GRAPH>;

/** A stored version of a workflow. `n` counts from 1, and the live version is the highest `n`. */
export const WORKFLOW_VERSION_RECORD = BASE.extend({
  workflowId: z.string(),
  n: z.number().int(),
  graph: WORKFLOW_GRAPH,
  /** Unset while the version waits for approval (X6-12b). */
  publishedAt: ISO.optional(),
  /** On a pending version, the person who asked to publish it; on a live one, the person who published it. */
  publishedBy: z.string(),
  pending: z.boolean().optional(),
  rejected: z.boolean().optional(),
});
export type WorkflowVersionRecord = z.infer<typeof WORKFLOW_VERSION_RECORD>;

export const RUN_STEP = z.object({
  nodeId: z.string(),
  state: RUN_STATE,
  ms: z.number(),
  input: z.record(z.string(), z.string()),
  output: z.record(z.string(), z.string()),
  error: z.string().optional(),
});
export type RunStep = z.infer<typeof RUN_STEP>;

/** One execution of a workflow on a single record. `trigger` names the event or schedule that started it; `dry` marks a test run that wrote nothing. */
export const RUN = BASE.extend({
  workflowId: z.string(),
  at: ISO,
  state: RUN_STATE,
  durationMs: z.number(),
  recordLabel: z.string(),
  trigger: z.string(),
  dry: z.boolean().default(false),
  steps: z.array(RUN_STEP),
  /** The published version the run follows (PHASED-PLAN F13); absent for workflows never published by the action. */
  version: z.number().int().optional(),
  /** Who or what started the run (PHASED-PLAN F16): a person id, "schedule", "webhook" or "event". The run's own writes are audited as `workflow:<workflow id>`. */
  actor: z.string().optional(),
  /** The chain of runs this run belongs to: a run's own writes never start a flow already in `chainFlows` (loop guard). */
  chainId: z.string().optional(),
  chainFlows: z.array(z.string()).optional(),
  /** Set on a run that another run called as a sub-workflow: the caller's run, its step, and how deep the call is (at most 3). */
  parentRunId: z.string().optional(),
  parentNodeId: z.string().optional(),
  depth: z.number().int().optional(),
  /** Why a run was skipped (rate limit, loop guard), in plain words. */
  note: z.string().optional(),
  /** On a test run, the person who gets the real mails of the run (the "send to me" test); nothing else is written. */
  testTo: z.string().optional(),
  /** On a test run given JSON input instead of a record, the input as JSON text: the run's trigger carries it as `input`. */
  testInput: z.string().max(65536).optional(),
});
export type Run = z.infer<typeof RUN>;

/** Status of one node inside one run, as the run inspector lists it. */
export const STEP_STATUS = z.enum(["queued", "running", "done", "failed", "skipped"]);
export type StepStatus = z.infer<typeof STEP_STATUS>;

/** One node's execution inside one run. `n` is the order in the run; `attempt` counts retries from 1. */
export const RUN_STEP_RECORD = BASE.extend({
  runId: z.string(),
  stepId: z.string(),
  n: z.number().int(),
  status: STEP_STATUS,
  input: z.record(z.string(), z.string()),
  output: z.record(z.string(), z.string()).optional(),
  error: z.string().optional(),
  attempt: z.number().int(),
  startedAt: ISO.optional(),
  endedAt: ISO.optional(),
});
export type RunStepRecord = z.infer<typeof RUN_STEP_RECORD>;

/** One block of a message template (X11-524a), shown in order: a heading, text, a button, an image or a divider. Text takes
 *  merge fields. A button links to http, https or mailto (or to a merge field that holds the link); an image is https only. */
export const TEMPLATE_BLOCK = z.object({
  kind: z.enum(["heading", "text", "button", "image", "divider"]),
  text: z.string().max(2000).optional(),
  href: z.string().max(2048).regex(/^(https?:\/\/|mailto:|\{\{)/i).optional(),
  src: z.string().max(2048).regex(/^(https:\/\/|\{\{)/i).optional(),
});
export type TemplateBlock = z.infer<typeof TEMPLATE_BLOCK>;

/** A saved text of a message template. `n` counts from 1, and the live text is the highest `n`. */
export const TEMPLATE_VERSION = z.object({
  n: z.number().int(),
  body: z.string(),
  /** Email templates only: the subject saved with this text. */
  subject: z.string().optional(),
  /** Email templates only (X11-524a): the blocks saved with this text. */
  blocks: z.array(TEMPLATE_BLOCK).max(60).optional(),
  createdAt: ISO,
  createdBy: z.string(),
});
export type TemplateVersion = z.infer<typeof TEMPLATE_VERSION>;

/** A client-facing message that automations send. `body` uses `{{client.name}}`-style variables. */
export const MESSAGE_TEMPLATE = BASE.extend({
  name: z.string(),
  moment: z.string(),
  channel: TEMPLATE_CHANNEL,
  enabled: z.boolean(),
  /** WhatsApp templates only, set by Meta. */
  approval: TEMPLATE_APPROVAL.optional(),
  languages: z.array(z.string()),
  subject: z.string().optional(),
  body: z.string(),
  /** Email templates only (X11-524a): when set, the blocks make the email, in order and in the brand; without them the body does.
   *  null clears them (the editor sends null when the last block is removed, X11-524b). */
  blocks: z.array(TEMPLATE_BLOCK).max(60).nullable().optional(),
  usedBy: z.array(z.string()),
  versions: z.array(TEMPLATE_VERSION).default([]),
  /** WhatsApp Cloud API templates: the name Meta knows, its category, Meta's id and review status, the rejection reason, and the layout Meta reviews. */
  metaName: z.string().regex(/^[a-z0-9_]{1,512}$/).optional(),
  metaCategory: z.enum(["marketing", "utility", "authentication"]).optional(),
  metaId: z.string().optional(),
  metaStatus: z.string().max(60).optional(),
  metaReason: z.string().max(2000).optional(),
  header: z.string().max(60).optional(),
  footer: z.string().max(60).optional(),
  examples: z.array(z.string().max(200)).optional(),
  /** The event that sends this message in an automation (A4f-AUTO3, additive); the list shows it as Trigger. */
  trigger: z.string().max(200).optional(),
  /** When an automation last sent this message (A4f-AUTO3, set by the send path); the list shows it as Last sent. */
  lastSentAt: ISO.optional(),
});
export type MessageTemplate = z.infer<typeof MESSAGE_TEMPLATE>;

const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
/**
 * Quiet hours for automations (PHASED-PLAN F16): an outward step (email, WhatsApp, SMS, a connector action) that comes
 * due between `from` and `to` waits until `to`. The workspace default is read from the setting `automationQuietHours`;
 * a flow overrides it with `quietFrom`, `quietTo` and `quietTz` on its trigger node (`quiet: "off"` turns it off there).
 */
export const QUIET_HOURS = z.strictObject({ enabled: z.boolean(), from: HHMM, to: HHMM, tz: z.string().max(64).optional() });
export type QuietHours = z.infer<typeof QUIET_HOURS>;
